import React, { useState, useCallback } from "react";
import { IconButton, Tooltip, Box } from "@mui/material";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CheckIcon from "@mui/icons-material/Check";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

/**
 * CopyToClipboard – universelle Kopier-Komponente mit visuellem Feedback.
 *
 * Props:
 *  - text        (string)   – Der zu kopierende Text. Required.
 *  - tooltip     (string)   – Tooltip-Text vor dem Kopieren. Default: "Kopieren"
 *  - size        (string)   – IconButton-Größe: "small" | "medium" | "large". Default: "medium"
 *  - resetDelay  (number)   – Millisekunden bis der Status zurückgesetzt wird. Default: 2000
 *  - onCopied    (function) – Optionaler Callback nach erfolgreichem Kopieren.
 *
 * Verwendung:
 *  <CopyToClipboard text="Hallo Welt" />
 *  <CopyToClipboard text={lead.email} tooltip="E-Mail kopieren" size="small" />
 */

const STATES = {
  IDLE: "idle",
  COPIED: "copied",
  ERROR: "error",
};

const stateConfig = (iconSx) => ({
  [STATES.IDLE]: {
    icon: <ContentCopyOutlinedIcon sx={iconSx} />,
    tooltip: null,
    color: "default",
  },
  [STATES.COPIED]: {
    icon: <CheckIcon sx={iconSx} />,
    tooltip: "Kopiert",
    color: "success",
  },
  [STATES.ERROR]: {
    icon: <ErrorOutlineIcon sx={iconSx} />,
    tooltip: "Fehler beim Kopieren",
    color: "error",
  },
});

function CopyToClipboard({
  text,
  tooltip = "Kopieren",
  size = "medium",
  resetDelay = 2000,
  onCopied,
  iconSx,
}) {
  const [copyState, setCopyState] = useState(STATES.IDLE);

  const handleCopy = useCallback(async () => {
    if (copyState !== STATES.IDLE) return; // Doppelklick verhindern

    try {
      await navigator.clipboard.writeText(text);
      setCopyState(STATES.COPIED);
      onCopied?.();
    } catch {
      setCopyState(STATES.ERROR);
    } finally {
      setTimeout(() => setCopyState(STATES.IDLE), resetDelay);
    }
  }, [text, copyState, resetDelay, onCopied]);

  const config = stateConfig(iconSx)[copyState];
  const currentTooltip = copyState === STATES.IDLE ? tooltip : config.tooltip;

  return (
    <Tooltip title={currentTooltip} arrow>
      <IconButton
        component="span"
        size={size}
        onClick={handleCopy}
        color={config.color}
        sx={{
          transition: "color 0.2s ease, background-color 0.2s ease, transform 0.15s ease",
          "&:active": { transform: "scale(0.88)" },
          "& svg": {
            transition: "transform 0.2s ease",
            transform: copyState === STATES.COPIED ? "scale(1.15)" : "scale(1)",
          },
          ...(copyState === STATES.COPIED && {
            backgroundColor: (theme) => theme.palette.success.main + "18",
          }),
          ...(copyState === STATES.ERROR && {
            backgroundColor: (theme) => theme.palette.error.main + "18",
          }),
        }}
      >
        {config.icon}
      </IconButton>
    </Tooltip>
  );
}

export default CopyToClipboard;


// ---------------------------------------------------------------------------
// Beispiel-Integration in LeadList (E-Mail-Spalte der DataGrid):
// ---------------------------------------------------------------------------
//
// import CopyToClipboard from "./CopyToClipboard";
//
// {
//   field: "email",
//   headerName: "Email",
//   minWidth: 200,
//   flex: 1.5,
//   renderCell: (params) => (
//     <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
//       {params.value}
//       {params.value && (
//         <CopyToClipboard text={params.value} tooltip="E-Mail kopieren" size="small" />
//       )}
//     </Box>
//   ),
// }